import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  ActionForAuthentication,
  GoogleOneTapStashedPayload,
} from '../../types';
import { GOOGLE_GSI_SCRIPT_URL, GOOGLE_ONE_TAP_SESSION_KEY } from './constants';

type Options = {
  type: ActionForAuthentication;
  utmSource?: string;
};

const isOnOfficialOrigin = (): boolean => {
  const officialBaseUrl = process.env.REACT_APP_BASE_URL;
  if (!officialBaseUrl) return false;
  try {
    return new URL(officialBaseUrl).origin === window.location.origin;
  } catch {
    return false;
  }
};

let gsiScriptPromise: Promise<void> | null = null;

const loadGsiScript = (): Promise<void> => {
  if (typeof document === 'undefined') return Promise.resolve();
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gsiScriptPromise) return gsiScriptPromise;

  gsiScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GOOGLE_GSI_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gsiScriptPromise = null;
      reject(new Error('Failed to load Google Identity Services script'));
    };
    document.head.appendChild(script);
  });

  return gsiScriptPromise;
};

const isOneTapEligible = (type: ActionForAuthentication): boolean =>
  type === ActionForAuthentication.Login ||
  type === ActionForAuthentication.Signup;

const useGoogleOneTap = ({ type, utmSource }: Options): void => {
  const history = useHistory();

  // Keep the latest options in a ref so the credential callback always reads
  // current values without forcing the initializer effect to re-run on every
  // prop change. Re-running the effect tears down and re-prompts One Tap,
  // which counts against Google's per-user prompt cooldown.
  const optionsRef = useRef({
    type,
    utmSource,
  });
  optionsRef.current = {
    type,
    utmSource,
  };

  // Mount-only effect. `type` is stable per OAuthButtons mount (each parent
  // page passes a fixed action constant), so capturing it here is safe.
  useEffect(() => {
    if (!isOneTapEligible(type)) return undefined;
    // Suppress One Tap on any host other than our official base origin —
    // whitelabel domains will fail Google's origin check on credential submit.
    if (!isOnOfficialOrigin()) return undefined;

    const clientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
    if (!clientId) {
      // Silently no-op when the Client ID is not configured (e.g. local dev
      // without the env var). The existing OAuth-redirect buttons still work.
      return undefined;
    }

    let cancelled = false;

    const handleCredentialResponse = (response: { credential?: string }) => {
      if (cancelled || !response?.credential) return;

      const {
        type: currentType,
        utmSource: currentUtmSource,
      } = optionsRef.current;
      const referralId = window.tolt_referral ?? null;

      const stashed: GoogleOneTapStashedPayload = {
        credential: response.credential,
        action: currentType,
        ...(currentUtmSource ? { utmSource: currentUtmSource } : {}),
        ...(currentType === ActionForAuthentication.Signup && referralId
          ? { rewardfulReferralId: referralId }
          : {}),
      };

      try {
        window.sessionStorage.setItem(
          GOOGLE_ONE_TAP_SESSION_KEY,
          JSON.stringify(stashed),
        );
      } catch {
        // SessionStorage may be unavailable in private modes — fall back to
        // letting the user retry via the OAuth-redirect button.
        return;
      }

      history.push('/auth/oauth-authenticate-callback/google?source=one-tap');
    };

    loadGsiScript()
      .then(() => {
        if (cancelled) return;
        const gsi = (window as any).google?.accounts?.id;
        if (!gsi) return;

        gsi.initialize({
          client_id: clientId,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: false,
          // Lets Safari (and other ITP browsers) show the One Tap prompt via
          // Google's cookie-free fallback. No-op on non-ITP browsers.
          itp_support: true,
          // Opt into the browser-native FedCM API on Chromium 117+. Google
          // automatically falls back to the classic iframe on browsers that do
          // not support FedCM (Safari, Firefox). Forward-compatible with
          // Chrome's planned deprecation of the legacy iframe One Tap.
          use_fedcm_for_prompt: true,
        });
        gsi.prompt();
      })
      .catch((error) => {
        console.error('Failed to load Google Identity Services script', error);
        // Script failed to load — One Tap silently disabled. Existing buttons
        // continue to work.
      });

    return () => {
      cancelled = true;
      try {
        (window as any).google?.accounts?.id?.cancel();
      } catch {
        /* no-op */
      }
    };
    // Mount-only on purpose — see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useGoogleOneTap;
